import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    DialogContent,
    DialogTitle,
    FormControl,
    FormLabel,
    IconButton,
    Input,
    Modal,
    ModalClose,
    ModalDialog,
    Stack,
    Table,
    Typography,
} from '@mui/joy';
import { Add, Delete, Edit } from '@mui/icons-material';
import { PageStandard } from '../utils/PageStandard';
import { PaymentRecipient } from '../utils/Model';
import {
    createPaymentRecipient,
    deletePaymentRecipient,
    getPaymentRecipients,
    updatePaymentRecipient,
} from '../utils/ViewModel';

interface FormState {
    name: string;
    stripe_account_id: string;
    email: string;
}

const emptyForm: FormState = { name: '', stripe_account_id: '', email: '' };

export function PaymentRecipients() {
    const [recipients, setRecipients] = useState<PaymentRecipient[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // null = chiuso, undefined = creazione, PaymentRecipient = modifica
    const [modal, setModal] = useState<PaymentRecipient | null | undefined>(null);
    const [form, setForm] = useState<FormState>(emptyForm);

    useEffect(() => { load(); }, []);

    async function load() {
        setLoading(true);
        setRecipients(await getPaymentRecipients());
        setLoading(false);
    }

    function openCreate() {
        setForm(emptyForm);
        setModal(undefined);
    }

    function openEdit(r: PaymentRecipient) {
        setForm({ name: r.name, stripe_account_id: r.stripe_account_id, email: r.email || '' });
        setModal(r);
    }

    function closeModal() { setModal(null); }

    async function handleSave() {
        if (!form.name.trim() || !form.stripe_account_id.trim()) {
            alert('Nome e Stripe Connect account id sono obbligatori');
            return;
        }
        setSaving(true);
        let ok: boolean;
        if (modal === undefined) {
            ok = await createPaymentRecipient({
                name: form.name.trim(), stripe_account_id: form.stripe_account_id.trim(), email: form.email.trim() || undefined,
            });
        } else {
            ok = await updatePaymentRecipient(modal!.id, {
                name: form.name.trim(), stripe_account_id: form.stripe_account_id.trim(), email: form.email.trim() || null, active: modal!.active,
            });
        }
        setSaving(false);
        if (ok) { closeModal(); await load(); }
    }

    async function handleToggleActive(r: PaymentRecipient) {
        if (await updatePaymentRecipient(r.id, { name: r.name, stripe_account_id: r.stripe_account_id, email: r.email, active: !r.active })) {
            await load();
        }
    }

    async function handleDelete(id: string) {
        if (!window.confirm('Eliminare questo destinatario? Possibile solo se non è ancora stato usato in nessun preventivo.')) return;
        if (await deletePaymentRecipient(id)) await load();
    }

    const isCreate = modal === undefined;

    return (
        <PageStandard
            title="Pagamenti — Destinatari"
            sidebarVisible={true}
            headerEnd={
                <Button size="sm" startDecorator={<Add />} onClick={openCreate}>
                    Nuovo destinatario
                </Button>
            }
        >
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                I locali/partner verso cui una voce di un preventivo di pagamento può essere trasferita via Stripe
                Connect. L'account Connect (<code>acct_...</code>) va creato/onboardato fuori da questo portale — qui
                si incolla solo l'id risultante.
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Table aria-label="Destinatari pagamenti" size="md" stickyHeader>
                    <thead>
                        <tr>
                            <th style={{ width: '25%' }}>Nome</th>
                            <th style={{ width: '30%' }}>Stripe Connect account</th>
                            <th style={{ width: '20%' }}>Email</th>
                            <th style={{ width: '10%' }}>Stato</th>
                            <th style={{ width: '15%' }}>Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {recipients.map((r) => (
                            <tr key={r.id}>
                                <td><Typography level="title-sm">{r.name}</Typography></td>
                                <td><Typography level="body-xs" fontFamily="monospace">{r.stripe_account_id}</Typography></td>
                                <td><Typography level="body-sm">{r.email || '—'}</Typography></td>
                                <td>
                                    <Chip size="sm" color={r.active ? 'success' : 'neutral'} sx={{ cursor: 'pointer' }} onClick={() => handleToggleActive(r)}>
                                        {r.active ? 'Attivo' : 'Disattivo'}
                                    </Chip>
                                </td>
                                <td>
                                    <Stack direction="row" spacing={1}>
                                        <IconButton size="sm" variant="soft" onClick={() => openEdit(r)}>
                                            <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton size="sm" variant="soft" color="danger" onClick={() => handleDelete(r.id)}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                </td>
                            </tr>
                        ))}
                        {!recipients.length && (
                            <tr>
                                <td colSpan={5}>
                                    <Typography level="body-sm" color="neutral" sx={{ py: 2, textAlign: 'center' }}>
                                        Nessun destinatario ancora creato.
                                    </Typography>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            )}

            <Modal open={modal !== null} onClose={closeModal}>
                <ModalDialog minWidth={420}>
                    <ModalClose />
                    <DialogTitle>{isCreate ? 'Nuovo destinatario' : 'Modifica destinatario'}</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <FormControl required>
                                <FormLabel>Nome</FormLabel>
                                <Input
                                    value={form.name}
                                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                    placeholder="es. Locale A"
                                />
                            </FormControl>
                            <FormControl required>
                                <FormLabel>Stripe Connect account id</FormLabel>
                                <Input
                                    value={form.stripe_account_id}
                                    onChange={(e) => setForm((f) => ({ ...f, stripe_account_id: e.target.value }))}
                                    placeholder="acct_..."
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Email (opzionale)</FormLabel>
                                <Input
                                    value={form.email}
                                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                />
                            </FormControl>
                            <Button loading={saving} onClick={handleSave}>
                                {isCreate ? 'Crea' : 'Salva'}
                            </Button>
                        </Stack>
                    </DialogContent>
                </ModalDialog>
            </Modal>
        </PageStandard>
    );
}
